import { Staff } from '@/types/person';
import { StaffUserSession } from '@/types/staffSession';
import { UserPermissions } from '@/types/user';

export type ErrorsValue = Record<string, string[]>;

export interface BillingReport {
  id: string;
  name: string;
}

export interface BillingReportRow {
  billing_item_id: string;
  service_id: string;
  full_name: string;
  person_id: string;
  service_date: string;
  service_type: string;
  destination: string;
  cpt_code: string;
  rate: number;
  units: number;
  total_charged: number;
  total_expected_revenue: number;
  modifiers: string;
  period_start_at: string;
  period_end_at: string;
  encounters_count: number;
  jurisdiction: string;
  admitted_at: string;
  sex: string;
  program_name: string;
  program_scope: string;
  billing_provider_name_id: string | null;
  phase_name: string;
  rendering_provider: string;
  rendering_provider_other_id_qualifier: string;
  rendering_provider_other_id_number: string;
  servicing_provider: string;
  servicing_provider_credentials: string;
  servicing_provider_role: string;
  primary_insurance_plan: string;
  insurance_policy: string;
  plan: string;
  priority_type: string;
  discharged_at: string;
  insurance_type: string;
  actual_duration: number;
  actual_duration_source: string;
  scheduled_duration: number;
  attendees: string;
  session_status: string;
  conducted_via: string;
  complexity_level: string;
  drug_screen_kind: string;
  drug_screen_result: string;
  chart_id: string;
  chart_filled_by: string;
  chart_locked_by: string;
  chart_locking_role: string;
  rendering_provider_mode: string;
  service_location: string;
  diagnosis_code1: string;
  diagnosis_code2: string;
  diagnosis_code3: string;
  diagnosis_code4: string;
  service_class: string;
  appointment_id: string;
  healthie_errors: string;
  ignore_error_at: string;
  pending_cms_1500_healthie_id: string | null;
  manually_associated_by: string;
  manually_associated_at: string;
  manually_held_at: string;
  errors: ErrorsValue;
  warnings: ErrorsValue;
  indicators?: string[];
  on_hold: boolean;
  is_case_rate_encounter: boolean;
  cms_1500_healthie_id: string;
  claim_group_id: string;
  claim_id: string;
  isRefreshing?: boolean;
  isSubmitting?: boolean;
}

export interface BillingReportResult {
  id: string;
  billing_report: {
    id: string;
    range_start: string;
    range_end: string;
    name: string;
    jurisdiction?: string;
    person_id?: string;
  };
  processed_by: Staff;
  row_count: number;
  created_at: string;
  updated_at: string;
  type: string;
  status: string;

  processed_started_at: string;
  processed_completed_at: string;
  error_count: number;
  error_rate: number;
  submitted_count: number;
  submitted_rate: number;
  amount_charged_in_cents: number;
  number_of_generated_claims: number;
}

export interface Props {
  healthie_url: string;
  billing_report_id: string;
  billing_report_result_id: string;
  user: StaffUserSession;
  permissions: UserPermissions;
}

export interface UiMessage {
  message: React.ReactNode;
  type: 'error' | 'success' | 'info';
  // Optional in-app action rendered alongside the message (e.g. "Undo"). Kept
  // as a label + callback -- rather than baked into `message` as a button --
  // so string-only surfaces like the app toast system can render it too.
  actionText?: string;
  onAction?: () => void;
}

export interface ReasonsByType {
  [key: string]: Reason[];
}

export interface ErrorsByField {
  [field: string]: {
    count: number;
    errors: { [message: string]: number };
  };
}

/**
 * Normalized Alert Summary model rendered by AlertSummaryList. Produced either
 * by aggregating loaded rows client-side (BillingReportErrorSummary, the legacy
 * report view) or straight off the billing items summary endpoint, which counts
 * the whole filtered set rather than just the rows in memory.
 */
export interface AlertMessageCount {
  description: string;
  count: number;
  // BillingAlert id -- only present on server-aggregated summaries, and what
  // click-to-filter uses to narrow the grid (the `alert_ids` filter param).
  alert_id?: number;
}

export interface AlertGroupSummary {
  group: string;
  count: number;
  messages: AlertMessageCount[];
}

export interface BillingItemAlertSummary {
  errors: AlertGroupSummary[];
  warnings: AlertGroupSummary[];
}

export interface Reason {
  id: string;
  name: string;
}

// Custom event types for billing report operations
export interface RefreshStartedEvent {
  type: 'refresh_started';
  ids: string[];
}

export interface RefreshCompleteEvent {
  type: 'refresh_complete';
  add_ids?: string[];
  remove_ids?: string[];
  update_ids?: string[];
  // IDs that were requested but couldn't be resolved to a billing report
  // result (e.g. no longer associated with any report) -- included so
  // callers that optimistically mark all requested rows as refreshing can
  // clear that state instead of leaving them stuck.
  skip_ids?: string[];
}

export interface SubmitStartedEvent {
  type: 'submit_started';
  ids: string[];
}

export interface SubmitCompleteEvent {
  type: 'submit_complete';
  add_ids?: string[];
  remove_ids?: string[];
  update_ids?: string[];
  // IDs that were requested but couldn't be resolved to a billing report
  // result (e.g. no longer associated with any report) -- included so
  // callers that optimistically mark all requested rows as submitting can
  // clear that state instead of leaving them stuck.
  skip_ids?: string[];
}

export interface SubmitFailedEvent {
  type: 'submit_failed';
  ids: string[];
}

export interface RefreshFailedEvent {
  type: 'refresh_failed';
  ids: string[];
}

export interface RowUpdatedEvent {
  type: 'row_updated';
  updates: Partial<BillingReportRow>;
}

export type BillingReportEvent =
  | RefreshStartedEvent
  | RefreshCompleteEvent
  | SubmitStartedEvent
  | SubmitCompleteEvent
  | RowUpdatedEvent
  | SubmitFailedEvent
  | RefreshFailedEvent;

// Type guard functions
export function isRefreshStartedEvent(event: unknown): event is RefreshStartedEvent {
  return (
    event !== null &&
    typeof event === 'object' &&
    'type' in event &&
    event.type === 'refresh_started' &&
    'ids' in event &&
    Array.isArray(event.ids) &&
    event.ids.every((id: unknown) => typeof id === 'string')
  );
}

export function isRefreshCompleteEvent(event: unknown): event is RefreshCompleteEvent {
  return (
    event !== null &&
    typeof event === 'object' &&
    'type' in event &&
    event.type === 'refresh_complete'
  );
}

export function isSubmitStartedEvent(event: unknown): event is SubmitStartedEvent {
  return (
    event !== null &&
    typeof event === 'object' &&
    'type' in event &&
    event.type === 'submit_started' &&
    'ids' in event &&
    Array.isArray(event.ids) &&
    event.ids.every((id: unknown) => typeof id === 'string')
  );
}

export function isSubmitCompleteEvent(event: unknown): event is SubmitCompleteEvent {
  return (
    event !== null &&
    typeof event === 'object' &&
    'type' in event &&
    event.type === 'submit_complete'
  );
}

export function isSubmitFailedEvent(event: unknown): event is SubmitFailedEvent {
  return (
    event !== null &&
    typeof event === 'object' &&
    'type' in event &&
    event.type === 'submit_failed' &&
    'ids' in event &&
    Array.isArray(event.ids) &&
    event.ids.every((id: unknown) => typeof id === 'string')
  );
}

export function isRefreshFailedEvent(event: unknown): event is RefreshFailedEvent {
  return (
    event !== null &&
    typeof event === 'object' &&
    'type' in event &&
    event.type === 'refresh_failed' &&
    'ids' in event &&
    Array.isArray(event.ids) &&
    event.ids.every((id: unknown) => typeof id === 'string')
  );
}
